import { Button } from "@rulebase/core/components";
import { useCallback, useEffect, useRef, useState } from "react";
import { api2BaseURL, appBaseURL } from "../lib/env";
import { rulebaseApi2Request } from "../lib/rulebase-api2-client/client";

type AuthState = "loading" | "authenticated" | "unauthenticated";

const POLL_INTERVAL_MS = 3000;
const LOGIN_TIMEOUT_MS = 60_000;

export function AuthGate({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>("loading");
  const [waitingForLogin, setWaitingForLogin] = useState(false);
  const [loginTimedOut, setLoginTimedOut] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const popupRef = useRef<Window | null>(null);

  const checkSession = useCallback(async () => {
    try {
      await rulebaseApi2Request("/session");
      setAuthState("authenticated");
      return true;
    } catch {
      // On network errors, show unauthenticated UI so users can recover.
    }

    setAuthState("unauthenticated");
    return false;
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  useEffect(() => {
    if (!waitingForLogin) return;

    pollRef.current = setInterval(async () => {
      const authenticated = await checkSession();
      if (authenticated) {
        setWaitingForLogin(false);
        setLoginTimedOut(false);
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
        popupRef.current = null;
      }
    }, POLL_INTERVAL_MS);

    timeoutRef.current = setTimeout(() => {
      setWaitingForLogin(false);
      setLoginTimedOut(true);
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
      popupRef.current = null;
    }, LOGIN_TIMEOUT_MS);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [waitingForLogin, checkSession]);

  function handleLogin() {
    setLoginTimedOut(false);
    const url = new URL(`${api2BaseURL}/accounts/auth/workos`);
    url.searchParams.set("redirect_uri", appBaseURL);
    popupRef.current = window.open(
      url.toString(),
      "rulebase-login",
      "popup,width=500,height=700",
    );
    setWaitingForLogin(true);
  }

  function handleCancelWaitingForLogin() {
    setWaitingForLogin(false);
    setLoginTimedOut(false);
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
  }

  if (authState === "loading") {
    return null;
  }

  if (authState === "unauthenticated") {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 px-2 text-center">
        {waitingForLogin ? (
          <>
            <p className="text-secondary-foreground">
              Waiting for login to complete...
            </p>

            <Button
              variant="ghost"
              onClick={handleCancelWaitingForLogin}
              className="w-full py-4 text-lg font-semibold text-secondary-foreground"
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <p className="text-secondary-foreground">
              Log in to Rulebase to continue.
            </p>

            <Button
              onClick={handleLogin}
              className="w-full py-4 text-lg font-semibold"
            >
              Log in
            </Button>

            {loginTimedOut ? (
              <p className="text-sm text-warning-400">
                Login timed out. Please try again.
              </p>
            ) : null}
          </>
        )}
      </div>
    );
  }

  return children;
}
